import { useMutation, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import { includeKeysListAgreementLinks, includeKeysListPdfs, invalidateQueryAllInvoicesAndBatchObjects } from './apis';
import type {
  GetUnbatchedInvoiceAgreementParams,
  InvoiceBatchCreationWithInvoicesSchema,
  InvoiceBatchItemsSchema,
  InvoiceBatchMutationSchema,
  InvoiceBatchQueryAllSchema,
  InvoiceBatchQuerySchema,
} from './schemas/invoice-batch';

const invoiceBatchesListStaleTimeMs = 5 * 60 * 1000;
const invoiceBatchesListGcTimeMs = 30 * 60 * 1000;

export function getInvoiceBatchesQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['INVOICE_BATCHES', apiParamStr],
    staleTime: invoiceBatchesListStaleTimeMs,
    gcTime: invoiceBatchesListGcTimeMs,
    queryFn: async () => {
      const res = await apiClient.get<InvoiceBatchQueryAllSchema>({
        endpoint: `/api/v1/invoice-batches`,
        params: `${apiParamStr}`,
      });

      return res;
    },
  };
}

export function useGetInvoiceBatches(apiParamStr: string) {
  return useSuspenseQuery(getInvoiceBatchesQuery(apiParamStr));
}

export function useGetInvoiceBatchesInfinite(apiParamStr: string) {
  return useSuspenseInfiniteQuery<InvoiceBatchQueryAllSchema, ApiError>({
    queryKey: ['INVOICE_BATCHES', apiParamStr],
    initialPageParam: 1,
    staleTime: invoiceBatchesListStaleTimeMs,
    gcTime: invoiceBatchesListGcTimeMs,
    queryFn: ({ pageParam }) =>
      apiClient.get<InvoiceBatchQueryAllSchema>({
        endpoint: `/api/v1/invoice-batches`,
        params: `${apiParamStr}&page=${pageParam}`,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
  });
}

export function useGetInvoiceBatchSuspense(id: string) {
  return useSuspenseQuery(getInvoiceBatchQuery(id));
}

const invoiceBatchTableIncludeKeys = [
  ...includeKeysListAgreementLinks.map((key) => `invoices.${key}`),
  ...includeKeysListPdfs.map((key) => `invoices.${key}`),
  'invoices.latest_email_history',
];

const invoiceBatchTableIncludeStr = invoiceBatchTableIncludeKeys.join(',');

export function getInvoiceBatchForTableQuery(id: string) {
  return {
    queryKey: ['INVOICE_BATCH_TABLE', id],
    staleTime: invoiceBatchesListStaleTimeMs,
    queryFn: async () => {
      const res = await apiClient.get<{ data: InvoiceBatchQuerySchema }>({
        endpoint: `/api/v1/invoice-batches/${id}?include=${invoiceBatchTableIncludeStr}`,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function getInvoiceBatchQuery(id: string) {
  return {
    queryKey: ['INVOICE_BATCH', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InvoiceBatchQuerySchema }>({
        endpoint: `/api/v1/invoice-batches/${id}?include=printable_invoices_pdf,digital_invoices_pdf,invoices.partner.icon`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateInvoiceBatchQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['INVOICE_BATCH', id] });
};

export const useInvoiceBatchWithInvoicesCreateMutation = () =>
  useMutation<{ id: number }, ApiError, InvoiceBatchCreationWithInvoicesSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/create-invoice-batch-with-invoices',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InvoiceBatchQuerySchema }, ApiError, InvoiceBatchMutationSchema>({
    mutationFn: async ({ id: bId, ...payload }) => {
      return apiClient.request({
        endpoint: `/api/v1/invoice-batches/${id || bId}`,
        method: 'PUT',
        payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'invoice-batches', id] });
      invalidateQueryAllInvoicesAndBatchObjects();
    },
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVOICE_BATCH_DELETE'],
    mutationFn: async ({ id }) => {
      return apiClient.request({
        endpoint: `/api/v1/invoice-batches/${id}`,
        method: 'DELETE',
      });
    },
  });

export const useGetUnbatchedInvoiceAgreementMutation = () =>
  useMutation<{ data: InvoiceBatchItemsSchema[]; meta: unknown }, ApiError, GetUnbatchedInvoiceAgreementParams>({
    mutationFn: async (params) => {
      return queryClient.fetchQuery({
        queryKey: [
          'UNBATCHED_INVOICEABLE_AGREEMENTS',
          {
            ...params,
          },
        ],
        staleTime: 0,
        gcTime: Infinity,

        queryFn: async () => {
          const res = await apiClient.get<{ data: InvoiceBatchItemsSchema[]; meta: unknown }>({
            endpoint: '/api/v1/unbatched-invoiceable-agreements',
            params: { ...params },
          });

          return res;
        },
      });
    },
  });

export const useRegeneratePrintableInvoiceMutation = (id: string) =>
  useMutation<{ data: InvoiceBatchQuerySchema }, ApiError>({
    mutationFn: async () => {
      return apiClient.get({
        endpoint: `/api/v1/invoice-pdf/regenerate/invoice-batch/${id}`,
      });
    },
    onSuccess: () => {
      invalidateInvoiceBatchQuery(id);
      invalidateQueryAllInvoicesAndBatchObjects();
    },
  });

export const useGenerateDigitalInvoiceMutation = (id: string) =>
  useMutation<{ data: InvoiceBatchQuerySchema }, ApiError>({
    mutationFn: async () => {
      return apiClient.get({
        endpoint: `/api/v1/invoice-pdf/generate/digital-invoice-batch/${id}`,
      });
    },
    onSuccess: () => {
      invalidateInvoiceBatchQuery(id);
      invalidateQueryAllInvoicesAndBatchObjects();
    },
  });
